import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import FileService from "../../Services/fileService";
import styles from "./MediathequeSection.module.css";
import i18n from "../../Services/i18n";
import FolderTree from "../../Components/FolderTree/FolderTree";
import FileFolderPreview from "../../Components/FileFolderPreview/FileFolderPreview";
import ImagePreview from "../../Components/ImagePreview/ImagePreview";
import MediaFilter from "../../Components/MediaFilter/MediaFilter";
import listIcon from "../../Assets/Icons/list.png";
import gridIcon from "../../Assets/Icons/grid.png";
import LoadingSpinner from "../../LayoutComponents/LoadingSpinner";

const MediathequeSection = React.forwardRef(
  ({ compareMode, reverseCol, folderTreeData, initialData, initialSelectedFolder, services }, ref) => {
    const [fileFolderBroswerData, setFileFolderBroswerData] = useState([]);
    const [selectedFolder, setSelectedFolder] = useState(initialSelectedFolder);
    const [selectedImgPreviewUrl, setSelectedImgPreviewUrl] = useState(null);
    const [viewMode, setViewMode] = useState("grid"); // 'list' ou 'grid'

    const [isLoadingFolderPreview, setIsLoadingFolderPreview] = useState(false);
    const requestSeqRef = useRef(0);
    useEffect(() => {
      if (Array.isArray(initialData) && initialData.length > 0) {
        setFileFolderBroswerData(initialData);
      }
    }, [initialData]);

    useEffect(() => {
      if (!initialSelectedFolder) return;
      if (selectedFolder?.folderUId !== initialSelectedFolder.folderUId) {
        setSelectedImgPreviewUrl(null);
        setSelectedFolder(initialSelectedFolder);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSelectedFolder]);

    useEffect(() => {
      if (selectedFolder === undefined) return;
      const folderUId = selectedFolder?.folderUId ?? null;
      fetchFilePreview({ folderUId });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFolder]);

    useImperativeHandle(ref, () => ({
      getData: () => ({
        fileFolderBroswerData,
      }),
      getSelectedFolder: () => selectedFolder,
    }));

    const fetchFilePreview = async (filterObj = false) => {
      try {
        let seq = ++requestSeqRef.current;
        setIsLoadingFolderPreview(true);
        const files = await FileService.getFileFolderPreview(filterObj);
        if (seq === requestSeqRef.current) {
          setFileFolderBroswerData(files);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des fichiers :", error);
        setFileFolderBroswerData([]);
      } finally {
        setIsLoadingFolderPreview(false);
      }
    };

    const handleFileSelected = async (id) => {
      try {
        setSelectedImgPreviewUrl(null);
        if (!id) return;
        const imgPreviewUrl = await FileService.getFile(id);
        setSelectedImgPreviewUrl(imgPreviewUrl || null);
      } catch (error) {
        console.error("Erreur lors du chargement du fichier :", error);
      }
    };

    const renderFolderPreview = () => {
      if (isLoadingFolderPreview) {
        return (
          <div className={styles.loadingIndicator}>
            <LoadingSpinner />
          </div>
        );
      }

      return (
        <FileFolderPreview
          files={fileFolderBroswerData}
          viewMode={viewMode}
          smallIcon={compareMode}
          onFileSelected={handleFileSelected}
          canDelete={false}
        />
      );
    };

    return (
      <div className={styles.container}>
        <div
          className={`${styles.folderAndFilter} ${compareMode ? styles.compareModeFolderAndFilter : ""} ${
            reverseCol ? styles.reverseColFolderAndFilter : ""
          }`}>
          <div
            className={styles.folder}
            style={{
              pointerEvents: isLoadingFolderPreview ? "none" : "auto",
              opacity: isLoadingFolderPreview ? 0.5 : 1,
            }}>
            <div className={styles.sectionHeader}>
              <span>{i18n.t("MediathequePage.FolderBrowserHeader")}</span>
            </div>
            {
              <FolderTree
                folderTreeData={folderTreeData}
                preSelectFolder={selectedFolder}
                handleSelectFolder={(folder) => {
                  if (folder.folderUId !== selectedFolder?.folderUId) {
                    setIsLoadingFolderPreview(true);
                    setSelectedImgPreviewUrl(null);
                    setSelectedFolder(folder);
                  }
                }}
              />
            }
          </div>

          <div className={styles.sectionHeader} style={{ marginTop: "30px" }}>
            <span>{i18n.t("MediathequePage.FilterHeader")}</span>
          </div>
          <div className={styles.filterContainer}>
            {<MediaFilter folder={selectedFolder} refreshFolderPreview={fetchFilePreview} services={services} />}
          </div>
        </div>

        <div
          className={`${styles.resultPreviewAndPreview} ${
            compareMode ? styles.compareModeResultPreviewAndPreview : ""
          } ${reverseCol ? styles.reverseColResultPreviewAndPreview : ""}`}>
          <div className={styles.fileFolderPreview}>
            <div className={styles.sectionHeader}>
              <span>{i18n.t("MediathequePage.FileFolderBrowserPreviewHeader")}</span>
              <div className={styles.toolbar}>
                <img
                  src={listIcon}
                  alt="List View"
                  className={`${styles.viewModeButton} ${viewMode === "list" ? styles.viewModeSelected : ""}`}
                  onClick={() => setViewMode("list")}
                />
                <img
                  src={gridIcon}
                  alt="Grid View"
                  className={`${styles.viewModeButton} ${viewMode === "grid" ? styles.viewModeSelected : ""}`}
                  onClick={() => setViewMode("grid")}
                />
              </div>
            </div>
            {renderFolderPreview()}
          </div>

          <div className={styles.previewContainer}>
            <div className={styles.sectionHeader}>
              <span>{i18n.t("MediathequePage.PreviewHeader")}</span>
            </div>
            <ImagePreview imageUrl={selectedImgPreviewUrl} />
          </div>
        </div>
      </div>
    );
  }
);

export default MediathequeSection;
